import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, CircularProgress, TextField, useTheme } from '@mui/material'

import { FlexDiv } from '../../components'
import { ServiceRequestImage } from '../Maintenance/ServiceRequestImage'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { useLease } from '../../ctx/lease'
import LoadingGIF from '../../assets/loadingGif.gif'
import { LeaseStatus } from '../../types/lease'

export const CreateServiceRequest = () => {
    const [tenantDescription, setTenantDescription] = useState('')
    const [tenantImage, setTenantImage] = useState<File | null>(null)

    const [doValidate, setDoValidate] = useState(false)

    const theme = useTheme()
    const navigate = useNavigate()

    const { isLoading, createServiceRequest, getAIResults } =
        useServiceRequest()
    const { leaseList } = useLease()

    const InfoTitleStyle: React.CSSProperties = {
        fontSize: '15px',
        fontWeight: 500,
        marginBottom: theme.spacing(1),
    }

    const DivStyle: React.CSSProperties = {
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
    }

    // Get the current lease
    const lease = leaseList?.find((lease) => lease.status === LeaseStatus.CURRENT) ?? null

    if (isLoading) {
        return (
            <FlexDiv
                style={{
                    height: '100%',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <img
                    src={LoadingGIF}
                    style={{
                        maxHeight: '100%',
                        maxWidth: '100%',
                    }}
                />
            </FlexDiv>
        )
    }

    return (
        <FlexDiv
            direction="column"
            style={{
                marginRight: theme.spacing(2),
                marginLeft: theme.spacing(2),
                height: '100%',
            }}
        >
            <div style={{ ...DivStyle }}>
                <div>
                    <span
                        style={{
                            ...theme.typography.h6,
                            fontWeight: theme.typography.fontWeightBold,
                        }}
                    >
                        Create Service Request
                    </span>
                </div>
                <div>
                    <span
                        style={{
                            ...theme.typography.subtitle2,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Include a description of your problem and an image of
                        the problem if applicable!
                    </span>
                </div>
            </div>

            <div style={{ ...DivStyle }}>
                <div style={{ ...InfoTitleStyle }}>Description of Problem:</div>
                <TextField
                    variant="outlined"
                    value={tenantDescription}
                    onChange={(e) => setTenantDescription(e.target.value)}
                    style={{
                        width: '100%',
                    }}
                    size="small"
                    multiline
                    error={doValidate && tenantDescription === ''}
                    helperText={
                        doValidate && tenantDescription === '' && 'Required'
                    }
                />
            </div>

            <div style={{ ...DivStyle }}>
                <div style={{ ...InfoTitleStyle }}>Photo of Problem:</div>
                <FlexDiv style={{ flexDirection: 'row' }}>
                    <ServiceRequestImage
                        image={tenantImage}
                        setImage={(image) => setTenantImage(image)}
                        index={0}
                    />
                </FlexDiv>
            </div>

            <div style={{ flex: 1 }} />

            <FlexDiv
                style={{
                    margin: theme.spacing(2, 0, 2, 0),
                    justifyContent: 'center',
                    alignSelf: 'center',
                    width: '100%',
                }}
            >
                <Button
                    style={{
                        ...theme.typography.subtitle1,
                        flex: 1,
                        textTransform: 'none',
                        color: theme.palette.error.main,
                        border: `1px solid ${theme.palette.error.main}`,
                        borderRadius: theme.shape.borderRadius,
                        fontWeight: theme.typography.fontWeightBold,
                        marginRight: theme.spacing(5),
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        navigate('../')
                    }}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                {lease?.allow_service_request && (
                    <Button
                        style={{
                            ...theme.typography.subtitle1,
                            flex: 1,
                            textTransform: 'none',
                            color: theme.palette.primary.contrastText,
                            borderRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.primary.dark,
                            fontWeight: theme.typography.fontWeightBold,
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            if (tenantDescription === '') {
                                setDoValidate(true)
                                return
                            }

                            const formData = new FormData()
                            if (tenantImage) {
                                formData.append('image', tenantImage)
                            }
                            formData.append(
                                'tenant_description',
                                tenantDescription,
                            )
                            formData.append('lease', String(lease.id))

                            createServiceRequest(formData).then(
                                (serviceRequest) => {
                                    getAIResults(serviceRequest).finally(() => {
                                        navigate('../')
                                    })
                                },
                            )
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress
                                size={20}
                                style={{
                                    color: theme.palette.primary.contrastText,
                                }}
                            />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                )}
            </FlexDiv>
        </FlexDiv>
    )
}
